import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Divider,
  useTheme,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  ArrowBackIosNewOutlined,
  EditOutlined,
  PrintOutlined,
  LocalShippingOutlined,
  ImageOutlined,
  BusinessOutlined,
  CategoryOutlined,
  StraightenOutlined,
  ScaleOutlined,
  LocationOnOutlined,
  PersonOutlined,
  TagOutlined,
  CalendarTodayOutlined,
} from "@mui/icons-material";
import { tokens } from "../../theme";
import { getToken, base_url } from "../../config/config";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import Barcode from "react-barcode";

const ProductView = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { id } = useParams();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [barcodeDialogOpen, setBarcodeDialogOpen] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const response = await axios.get(`${base_url}/v1/stock/products/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setProduct(response.data.data);
      } else {
        setAlert({
          open: true,
          message: "Product not found",
          severity: "error",
        });
        navigate("/stock/products");
      }
    } catch (error) {
      console.error("Error fetching product:", error);
      setAlert({
        open: true,
        message: "Error loading product details",
        severity: "error",
      });
      navigate("/stock/products");
    } finally {
      setLoading(false);
    }
  };

  const handlePrintBarcode = () => {
    const printContent = document.getElementById("barcode-print");
    const WinPrint = window.open("", "", "width=600,height=600");
    WinPrint.document.write(
      "<html><head><title>Barcode Print</title></head><body>"
    );
    WinPrint.document.write(printContent.innerHTML);
    WinPrint.document.write("</body></html>");
    WinPrint.document.close();
    WinPrint.focus();
    WinPrint.print();
    WinPrint.close();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("de-DE", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStockStatus = () => {
    if (!product) return { color: "default", text: "Unknown" };

    if (product.stockQuantity <= 0) {
      return { color: "error", text: "Out of Stock" };
    } else if (product.stockQuantity <= product.minStockLevel) {
      return { color: "warning", text: "Low Stock" };
    } else {
      return { color: "success", text: "In Stock" };
    }
  };

  const getLocationDisplay = (location) => {
    const locations = {
      stock: "Hauptlager",
      production: "Produktionsbereich",
      quality: "Qualitätskontrolle",
    };
    return locations[location] || location;
  };

  const generateBarcodeValue = (product) => {
    const ownerName = product.stockOwner?.name || "Unknown";
    const diameter = product.diameter || "0";
    const location = product.location || "Unknown";

    // Combine the three values with a separator (you can change the separator if needed)
    return `${ownerName}-${diameter}-${location}`;
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="80vh"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!product) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="80vh"
      >
        <Typography variant="h4" color={colors.grey[500]}>
          Product not found
        </Typography>
      </Box>
    );
  }

  const stockStatus = getStockStatus();

  return (
    <Box m="20px">
      {/* HEADER */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box>
          <Typography
            variant="h2"
            color={colors.grey[100]}
            fontWeight="bold"
            mb="5px"
          >
            {product.name}
          </Typography>
          <Typography variant="h5" color={colors.greenAccent[400]}>
            Product Details • SKU: {product.artikelNummer}
          </Typography>
        </Box>

        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ArrowBackIosNewOutlined />}
            onClick={() => navigate("/stock/products")}
          >
            Back to Products
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<LocalShippingOutlined />}
            onClick={() =>
              navigate("/stock/movements/new", {
                state: { productId: product._id },
              })
            }
          >
            Create Movement
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<EditOutlined />}
            onClick={() => navigate(`/stock/products/edit/${product._id}`)}
          >
            Edit Product
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* LEFT COLUMN - Product Image & Barcode */}
        <Grid item xs={12} md={4}>
          {/* Product Image */}
          <Card sx={{ mb: 3 }}>
            <Box
              sx={{
                height: 300,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: colors.primary[500],
                position: "relative",
              }}
            >
              {product.image?.s3Url ? (
                <CardMedia
                  component="img"
                  image={product.image.s3Url}
                  alt={product.name}
                  sx={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                  }}
                />
              ) : (
                <ImageOutlined
                  sx={{ fontSize: 100, color: colors.grey[500] }}
                />
              )}
            </Box>
            {product.image && (
              <CardContent>
                <Typography variant="body2" color={colors.grey[300]}>
                  Image: {product.image.originalName}
                </Typography>
                <Typography variant="caption" color={colors.grey[400]}>
                  Size: {(product.image.size / 1024).toFixed(1)} KB • Type:{" "}
                  {product.image.mimeType}
                </Typography>
              </CardContent>
            )}
          </Card>

          {/* Barcode Card */}
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="h6" mb={2}>
                Product Barcode
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  bgcolor: "#fff",
                  p: 2,
                  borderRadius: 1,
                  mb: 2,
                }}
              >
                <Barcode
                  value={generateBarcodeValue(product)}
                  height={30}
                  width={1}
                  fontSize={12}
                />
              </Box>
              <Typography variant="body2" color={colors.grey[300]} mb={2}>
                Barcode: {generateBarcodeValue(product)}
              </Typography>
              <Button
                variant="outlined"
                startIcon={<PrintOutlined />}
                onClick={() => setBarcodeDialogOpen(true)}
                fullWidth
              >
                Print Barcode
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* MIDDLE COLUMN - Product Information */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, mb: 3, bgcolor: colors.primary[400] }}>
            <Typography variant="h4" mb={3}>
              Product Information
            </Typography>

            {/* Basic Info */}
            <Box mb={3}>
              <Box display="flex" alignItems="center" mb={2}>
                <CategoryOutlined
                  sx={{ mr: 1, color: colors.greenAccent[400] }}
                />
                <Typography variant="h6">Basic Details</Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" color={colors.grey[300]}>
                    Description
                  </Typography>
                  <Typography variant="body1" mb={1}>
                    {product.description || "No description provided"}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body2" color={colors.grey[300]}>
                    Unit
                  </Typography>
                  <Typography variant="body1">{product.unitName}</Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body2" color={colors.grey[300]}>
                    Material Type
                  </Typography>
                  <Typography variant="body1">
                    {product.materialType?.name || "Not specified"}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Stock Information */}
            <Box mb={3}>
              <Box display="flex" alignItems="center" mb={2}>
                <ScaleOutlined sx={{ mr: 1, color: colors.greenAccent[400] }} />
                <Typography variant="h6">Stock Information</Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Typography variant="body2" color={colors.grey[300]}>
                    Current Stock
                  </Typography>
                  <Typography
                    variant="h6"
                    color={
                      stockStatus.color === "error"
                        ? colors.redAccent[500]
                        : colors.grey[100]
                    }
                  >
                    {product.stockQuantity}
                  </Typography>
                </Grid>

                <Grid item xs={4}>
                  <Typography variant="body2" color={colors.grey[300]}>
                    Min Level
                  </Typography>
                  <Typography variant="h6">{product.minStockLevel}</Typography>
                </Grid>

                <Grid item xs={4}>
                  <Typography variant="body2" color={colors.grey[300]}>
                    Status
                  </Typography>
                  <Chip
                    label={stockStatus.text}
                    color={stockStatus.color}
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Pricing */}
            <Box mb={3}>
              <Box display="flex" alignItems="center" mb={2}>
                <BusinessOutlined
                  sx={{ mr: 1, color: colors.greenAccent[400] }}
                />
                <Typography variant="h6">Pricing</Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color={colors.grey[300]}>
                    Purchase Price
                  </Typography>
                  <Typography variant="h6">
                    {formatCurrency(product.purchasePrice)}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body2" color={colors.grey[300]}>
                    Total Value
                  </Typography>
                  <Typography variant="h6" color={colors.greenAccent[400]}>
                    {formatCurrency(
                      product.stockQuantity * product.purchasePrice
                    )}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {/* Tags */}
            {product.tags && product.tags.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box>
                  <Box display="flex" alignItems="center" mb={2}>
                    <TagOutlined
                      sx={{ mr: 1, color: colors.greenAccent[400] }}
                    />
                    <Typography variant="h6">Tags</Typography>
                  </Box>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {product.tags.map((tag, index) => (
                      <Chip
                        key={index}
                        label={tag}
                        size="small"
                        variant="outlined"
                        color="secondary"
                      />
                    ))}
                  </Box>
                </Box>
              </>
            )}
          </Paper>

          {/* Supplier Information */}
          {product.supplierInfo && product.supplierInfo.length > 0 && (
            <Paper sx={{ p: 3, bgcolor: colors.primary[400] }}>
              <Typography variant="h4" mb={3}>
                Supplier Information
              </Typography>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Supplier</TableCell>
                      <TableCell align="right">Price</TableCell>
                      <TableCell align="right">Lead Time</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {product.supplierInfo.map((supplier, index) => (
                      <TableRow key={index}>
                        <TableCell>{supplier.firmId || "N/A"}</TableCell>
                        <TableCell align="right">
                          {formatCurrency(supplier.price)}
                        </TableCell>
                        <TableCell align="right">
                          {supplier.leadTime} days
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Grid>

        {/* RIGHT COLUMN - Dimensions & Additional Info */}
        <Grid item xs={12} md={3}>
          {/* Dimensions */}
          <Paper sx={{ p: 3, mb: 3, bgcolor: colors.primary[400] }}>
            <Box display="flex" alignItems="center" mb={2}>
              <StraightenOutlined
                sx={{ mr: 1, color: colors.greenAccent[400] }}
              />
              <Typography variant="h5">Dimensions</Typography>
            </Box>

            {product.length && (
              <Box mb={2}>
                <Typography variant="body2" color={colors.grey[300]}>
                  Length
                </Typography>
                <Typography variant="body1">{product.length} mm</Typography>
              </Box>
            )}

            {product.diameter && (
              <Box mb={2}>
                <Typography variant="body2" color={colors.grey[300]}>
                  Diameter
                </Typography>
                <Typography variant="body1">{product.diameter} mm</Typography>
              </Box>
            )}

            {/* Wall Thickness */}
            {product.wallThickness && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" mb={2}>
                  Wall Thickness ({product.wallThickness.unit})
                </Typography>

                <Grid container spacing={1}>
                  {product.wallThickness.left && (
                    <Grid item xs={6}>
                      <Typography variant="body2" color={colors.grey[300]}>
                        Left
                      </Typography>
                      <Typography variant="body1">
                        {product.wallThickness.left}
                      </Typography>
                    </Grid>
                  )}

                  {product.wallThickness.right && (
                    <Grid item xs={6}>
                      <Typography variant="body2" color={colors.grey[300]}>
                        Right
                      </Typography>
                      <Typography variant="body1">
                        {product.wallThickness.right}
                      </Typography>
                    </Grid>
                  )}

                  {product.wallThickness.average && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color={colors.grey[300]}>
                        Average
                      </Typography>
                      <Typography
                        variant="body1"
                        color={colors.greenAccent[400]}
                      >
                        {product.wallThickness.average}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </>
            )}
          </Paper>

          {/* Location & Owner */}
          <Paper sx={{ p: 3, mb: 3, bgcolor: colors.primary[400] }}>
            <Box display="flex" alignItems="center" mb={2}>
              <LocationOnOutlined
                sx={{ mr: 1, color: colors.greenAccent[400] }}
              />
              <Typography variant="h5">Location & Owner</Typography>
            </Box>

            <Box mb={2}>
              <Typography variant="body2" color={colors.grey[300]}>
                Location
              </Typography>
              <Typography variant="body1">
                {getLocationDisplay(product.location)}
              </Typography>
            </Box>

            {product.stockOwner && (
              <Box mb={2}>
                <Typography variant="body2" color={colors.grey[300]}>
                  Owner
                </Typography>
                <Typography variant="body1">
                  {product.stockOwner.name} ({product.stockOwner.code})
                </Typography>
              </Box>
            )}
          </Paper>

          {/* Metadata */}
          <Paper sx={{ p: 3, bgcolor: colors.primary[400] }}>
            <Box display="flex" alignItems="center" mb={2}>
              <CalendarTodayOutlined
                sx={{ mr: 1, color: colors.greenAccent[400] }}
              />
              <Typography variant="h5">Metadata</Typography>
            </Box>

            <Box mb={2}>
              <Typography variant="body2" color={colors.grey[300]}>
                Created
              </Typography>
              <Typography variant="caption">
                {formatDate(product.createdAt)}
              </Typography>
            </Box>

            <Box mb={2}>
              <Typography variant="body2" color={colors.grey[300]}>
                Last Updated
              </Typography>
              <Typography variant="caption">
                {formatDate(product.updatedAt)}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color={colors.grey[300]}>
                Data Source
              </Typography>
              <Typography variant="body1">
                {product.dataSource || "Unknown"}
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* BARCODE PRINT DIALOG */}
      <Dialog
        open={barcodeDialogOpen}
        onClose={() => setBarcodeDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5" component="div">
            Print Barcode
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 1 }}>
          <Box
            id="barcode-print"
            sx={{
              textAlign: "center",
              p: 2,
              bgcolor: "#fff",
              color: "#000",
            }}
          >
            <Typography variant="h6" fontWeight="bold" mb={1}>
              {product.name}
            </Typography>

            <Box
              sx={{
                border: "1px solid #000",
                p: 1,
                mb: 1,
                display: "inline-block",
              }}
            >
              <Barcode
                value={generateBarcodeValue(product)}
                height={30}
                width={1}
                fontSize={12}
                margin={0}
              />
            </Box>

            <Typography variant="body2" fontWeight="bold" mb={1}>
              Barcode: {generateBarcodeValue(product)}
            </Typography>

            <Typography
              variant="caption"
              color="textSecondary"
              display="block"
              mb={0.5}
            >
              Besitzer: {product.stockOwner?.name || "Unbekannt"}
            </Typography>
            <Typography
              variant="caption"
              color="textSecondary"
              display="block"
              mb={0.5}
            >
              Durchmesser: {product.diameter || "0"} mm
            </Typography>
            <Typography
              variant="caption"
              color="textSecondary"
              display="block"
              mb={0.5}
            >
              Standort: {getLocationDisplay(product.location)}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              SKU: {product.artikelNummer}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 1 }}>
          <Button onClick={() => setBarcodeDialogOpen(false)} size="small">
            Close
          </Button>
          <Button
            onClick={handlePrintBarcode}
            startIcon={<PrintOutlined />}
            variant="contained"
            color="secondary"
            size="small"
          >
            Print
          </Button>
        </DialogActions>
      </Dialog>

      {/* ALERT SNACKBAR */}
      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={() => setAlert({ ...alert, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setAlert({ ...alert, open: false })}
          severity={alert.severity}
          sx={{ width: "100%" }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductView;
